import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { JsonSubType, plainToClass } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LangService } from 'src/app/common/service/lang.service';
import { Group } from 'src/app/management/model/group';
import { ReportFileRequestParams } from 'src/app/reports/model/report-file-request-params';
import { generateReportTitle } from 'src/app/reports/util/bus-stop-report-util';
import { SortBy } from 'src/app/shared/model/data-source';
import { ExportRequest } from 'src/app/shared/model/export-request';
import { Operator } from 'src/app/shared/model/operator';
import { RequestCriteria } from 'src/app/shared/model/request-criteria';
import { RestResponse } from 'src/app/shared/model/rest-response';
import { createTitlesAsString, createViewStateAsString } from 'src/app/shared/util/view-state-creator';
import { RestSimpleRequest } from '../../shared/model/rest-simple-request';
import { RestDataLoader } from '../../shared/util/rest-data-loader';
import { AddMultipleVehiclesResponse } from '../model/add-multiple-vehicles-response';
import { Device } from '../model/device';
import { Vehicle } from '../model/vehicle';
import { VehicleDeviceLocation } from '../model/vehicle-device-location';
import { VehicleLayout } from '../model/vehicle-layout';
import { ResourceNumericVersionIdentifier } from 'src/app/common/model/resource-id-version-identifier';

@Injectable()
export class VehiclesService extends RestDataLoader<Vehicle> {

  constructor(http: HttpClient, protected translate: TranslateService, protected langService: LangService) {
    super(http, translate);
  }

  sendCreateReportRequest(exportRequest: ExportRequest, visibleColumnNames: string[], sortBy: SortBy[], groupBy: string
  ): Observable<any> {

    const criteria: RequestCriteria = {
      operator: Operator.and,
      criteria: [
        {
          fieldName: 'active',
          operator: Operator.equals,
          value: String(this.onlyActive)
        },
        {
          fieldName: 'software_actual',
          operator: Operator.equals,
          value: String(this.isSoftwareCurrent)
        }
      ]
    };
    const reportName = 'Vehicles';
    const reportTitle = generateReportTitle(criteria, this.translate.instant(reportName), this.translate);
    const fileName = exportRequest.fileName?.trim() || 'Vehicles';
    const titles = createTitlesAsString(visibleColumnNames, this.translate);
    const viewState = createViewStateAsString(sortBy, visibleColumnNames, groupBy);

    const params: ReportFileRequestParams = {
      viewState,
      reportTitle,
      titles,
      file_type: exportRequest.fileType,
      filename: fileName,
      summaries: '{}',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Accept-Language': this.langService.getLanguage(),
      cron: exportRequest.cron,
      archive: false
    };

    const request: RestSimpleRequest<RequestCriteria> = { params, data: criteria };

    return this.doPost<void>('api/async/files/vehicles/criteria/generate', request);
  }

  private onlyActive: boolean;
  private isSoftwareCurrent: boolean;

  protected fetchRequest(_payload: any): Observable<RestResponse<Vehicle>> {
    const url = this.onlyActive ? 'api/vehicles/fetch' : 'api/vehicles/fetch/all';
    return this.postRequest<Vehicle>(Vehicle, url, {}).pipe(
      map(response => {
        const data = response.data ?? [];
        return {
          ...response,
          data: this.isSoftwareCurrent
            ? data.filter(v => v.isSoftwareCurrent == false)
            : data
        };
      })
    );
  }

  getVehicles(): Observable<Vehicle[]> {
    return this.postAndExtract<Vehicle>(Vehicle, 'api/vehicles/fetch', {});
  }

  showActiveOnly(onlyActive: boolean): void {
    this.onlyActive = onlyActive;
    this.sendRequest({});
  }

  showSoftwareNotCurrent(onlyCurrent: boolean): void {
    this.isSoftwareCurrent = onlyCurrent;
    this.sendRequest({});
  }

  addVehicle(values: any): Observable<Vehicle> {
    const payload: RestSimpleRequest = {
      data: {
        veh_side_number: values.sideNumber,
        active: values.active,
        groups: values.groups
      }
    };
    return this.postAndExtractSingle<Vehicle>(Vehicle, 'api/admin/vehicle/gwt/add', payload);
  }

  addMultipleVehicles(sideNumbers: string[], groups: Group[]): Observable<AddMultipleVehiclesResponse> {
    const payload: RestSimpleRequest = {
      data: { sideNumbers, groupIds: groups.map(group => group.groupId) }
    };
    return this.postAndExtractSingle<AddMultipleVehiclesResponse>(AddMultipleVehiclesResponse, 'api/admin/vehicle/gwt/addMultiple', payload);
  }

  updateVehicle(values: any): Observable<any> {
    const payload: RestSimpleRequest = {
      data: {
        veh_id: values.id,
        veh_side_number: values.sideNumber,
        active: values.active,
        groups: values.groups
      }
    };
    return this.putAndExtractSingle<Vehicle>(Vehicle, 'api/admin/vehicle/gwt/update', payload);
  }

  getVehiclesByCriteria(reqCriteria: RequestCriteria): Observable<Vehicle[]> {
    return this.postAndExtract<Vehicle>(Vehicle, 'api/vehicles/fetchVehiclesByCriteria', reqCriteria);
  }

  updateVehicleProperties(vehicleProperties: any): Observable<Vehicle> {
    const payload: RestSimpleRequest = {
      data: vehicleProperties
    };
    return this.putAndExtractSingle<Vehicle>(Vehicle, 'api/vehicles/gwt/updateProperty', payload);
  }

  getVehicleLayout(sideNumber: string): Observable<VehicleLayout> {
    return this.getAndExtractSingle<VehicleLayout>(VehicleLayout, `api/admin/vehicle/${sideNumber}/layout/gwt/fetch`);
  }

  updateVehicleDeviceLocations(locations: VehicleDeviceLocation[], sideNumber: string): Observable<VehicleDeviceLocation[]> {
    const payload: RestSimpleRequest = {
      data: locations
    };
    return this.putAndExtract<VehicleDeviceLocation>(VehicleDeviceLocation, `api/admin/vehicle/${sideNumber}/gwt/updateLocations`, payload);
  }

  getAllVehicleDevices(sideNumber: string): Observable<Device[]> {
    return this.postAndExtract<Device>(Device, `api/${sideNumber}/devices/fetch`);
  }

  updateVehiclesProperty(sideNumbers: string[], fieldName: string, value: string): Observable<Vehicle[]> {
    return this.doPut(`api/admin/vehicle/property/update?fieldName=${fieldName}&value=${value}`, sideNumbers)
      .pipe(map((request: JsonSubType[]) => request.map((jsonVeh: JsonSubType) => plainToClass(Vehicle, jsonVeh))));
  }

  updateAllVehiclesProperty(fieldName: string, value: string): Observable<Vehicle[]> {
    return this.doPut(`api/admin/vehicles/property/update/all?fieldName=${fieldName}&value=${value}`, null)
      .pipe(map((request: JsonSubType[]) => request.map((jsonVeh: JsonSubType) => plainToClass(Vehicle, jsonVeh))));
  }

  fetchAllSnvrVehicles(): Observable<Vehicle[]> {
    return this.postAndExtract(Vehicle, 'api/snvr/vehicles/fetch');
  }

  assignDepot(vehicleId: number, depotId: ResourceNumericVersionIdentifier | null): Observable<void> {
    const params = depotId != null ? { depotId: depotId.toString() } : {};
    return this.doPost(`api/vehicle/${vehicleId}/depot`, null, { params });
  };

}
